<script setup lang="ts">
import { MaskInput } from 'maska';
import type { Props } from '@/shared/ui/AppTextarea/model/types/appTextarea';

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  modelValue: '',
  rows: 3,
  disabled: false,
  error: '',
  attr: () => ({}),
  maxLength: 0,
  mask: '',
  submitOnEnter: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue' | 'input', value: string): void;
  (e: 'focus' | 'blur' | 'enter'): void;
  (e: 'keydown', event: KeyboardEvent): void;
}>();

const isActive: Ref<boolean> = ref(false);
const fieldRef: Ref<HTMLTextAreaElement | null> = ref(null);
const maskId = useId();
let maskInput: MaskInput | null = null;

const remaining: ComputedRef<number> = computed(
  () => props.maxLength - String(props.modelValue).length,
);

const handleInput = (event: Event): void => {
  const target = event.target;
  if (!(target instanceof HTMLTextAreaElement)) return;
  emit('update:modelValue', target.value);
  emit('input', target.value);
};

const focusHandler = (): void => {
  isActive.value = true;
  emit('focus');
};

const blurHandler = (): void => {
  isActive.value = false;
  emit('blur');
};

const keydownHandler = (event: KeyboardEvent): void => {
  emit('keydown', event);
  if (props.submitOnEnter && event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    emit('enter');
  }
};

const focus = (): void => {
  fieldRef.value?.focus();
};

const getElement = (): HTMLTextAreaElement | null => fieldRef.value;

const applyMask = (mask: string): void => {
  if (!mask) {
    maskInput?.destroy();
    maskInput = null;
    return;
  }
  if (maskInput) {
    maskInput.update({ mask });
    return;
  }
  if (fieldRef.value) {
    maskInput = new MaskInput(`[data-mask-id="${maskId}"]`, { mask });
  }
};

watch(() => props.mask, applyMask);

onMounted(() => {
  applyMask(props.mask);
});

onBeforeUnmount(() => {
  maskInput?.destroy();
  maskInput = null;
});

defineExpose({ focus, getElement });
</script>

<template>
  <div class="app-textarea">
    <textarea
      ref="fieldRef"
      :data-mask-id="maskId"
      :value="modelValue"
      :class="[
        'app-textarea__field',
        { _error: error },
        { _disabled: disabled },
        { _active: isActive },
        { '_with-counter': maxLength > 0 },
      ]"
      :rows="rows"
      :placeholder="placeholder"
      :disabled="disabled"
      :maxlength="maxLength > 0 ? maxLength : undefined"
      v-bind="attr"
      @input="handleInput"
      @focus="focusHandler"
      @blur="blurHandler"
      @keydown="keydownHandler"
    ></textarea>
    <span
      v-if="maxLength > 0"
      class="app-textarea__counter"
      data-testid="textarea-counter"
      >{{ remaining }}</span
    >
    <span v-if="error" class="app-textarea__error" data-testid="textarea-error">{{
      error
    }}</span>
  </div>
</template>

<style lang="less">
.app-textarea {
  position: relative;
  width: 100%;

  &__field {
    .input-reset();

    display: block;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    color: @color-elephant;
    background-color: #fff;
    width: 100%;
    min-height: 90px;
    border-radius: @main-size;
    border: 1px solid @color-gallery;
    padding: @size-x3 @size-x4;
    resize: none;
    transition: border-color 0.3s ease;

    &::placeholder {
      color: @color-dove-gray;
    }

    &._error {
      background-color: #fef3ee;
      border: 1px solid @color-crimson;
    }

    &._disabled {
      pointer-events: none;
      color: @color-silver-chalice;

      &::placeholder {
        color: @color-silver-chalice;
      }
    }

    &._active:not(._error) {
      border: 1px solid @color-persimmon;
    }

    &._with-counter {
      padding-right: 60px;
    }
  }

  &__counter {
    position: absolute;
    top: 10px;
    right: @size-x5;
    font-size: @size-x3;
    line-height: @size-x4;
    font-weight: 500;
    color: @color-dove-gray;
    pointer-events: none;
  }

  &__error {
    display: block;
    font-size: @size-x3;
    line-height: @size-x4;
    font-weight: 500;
    color: @color-crimson;
    margin-top: @size-x3;
  }
}
</style>
